import { lazy, ComponentType } from "react"

/**
 * Wraps a lazy import with retry logic to handle failed dynamic imports.
 * This is useful for handling network issues or CDN caching problems in production.
 *
 * @param importFn - The function that returns a dynamic import promise
 * @param retries - Number of retry attempts (default: 3)
 * @param retryDelay - Delay between retries in milliseconds (default: 1000)
 * @returns A lazy component with retry logic
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  retryDelay = 1000
): React.LazyExoticComponent<T> {
  return lazy(() => {
    const attemptImport = (attempt: number): Promise<{ default: T }> => {
      return importFn().catch((error) => {
        // If this is the last attempt, throw the error
        if (attempt >= retries) {
          console.error(
            `Failed to load module after ${retries} attempts:`,
            error
          )
          throw error
        }

        // Log the retry attempt
        console.warn(
          `Failed to load module (attempt ${attempt}/${retries}), retrying in ${retryDelay}ms...`,
          error
        )

        // Wait before retrying
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(attemptImport(attempt + 1))
          }, retryDelay)
        })
      })
    }

    return attemptImport(1)
  })
}
